import React, { memo } from 'react';
import PropTypes from 'prop-types';
import Card from '@mui/material/Card';
import { styled } from '@mui/material/styles';
import { hasValue } from 'swipeclock-frontend';

const StyledCard = styled(Card, {
  shouldForwardProp: (prop) =>
    prop !== 'accentColor' &&
    prop !== 'gap' &&
    prop !== 'customColor' &&
    prop !== 'isEnclosedMode',
})(({ theme, accentColor, gap = 2, customColor, isEnclosedMode }) => {
  const backgroundColor = hasValue(customColor)
    ? customColor
    : accentColor?.main || theme.palette.primary.main;
  return {
    width: isEnclosedMode ? '100%' : '90vw',
    display: 'grid',
    gap: theme.spacing(gap),
    borderRadius: theme.shape.borderRadius,
    boxShadow: isEnclosedMode ? 'none' : theme.shadows[4],
    position: 'relative',
    padding: theme.spacing(4),
    overflow: 'hidden',
    minHeight: '33vh',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '4px',
      background: isEnclosedMode ? 'none' : backgroundColor,
    },
    [theme.breakpoints.down('mobile')]: {
      width: '100vw',
      minHeight: '100vh',
      placeItems: 'flex-start',
      overflowY: 'auto',
    },
    [theme.breakpoints.up('sm')]: {
      width: isEnclosedMode ? '100%' : 512,
      margin: '0 auto',
    },
  };
});

/**
 * PromptContentCard component renders a styled card container for prompt content.
 * Features responsive design, custom accent colors, and consistent spacing.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - The content to be rendered inside the card
 * @param {Object} [props.accentColor] - Accent color object with main and light properties
 * @param {string} [props.accentColor.main] - Main accent color for the card's top border
 * @param {string} [props.accentColor.light] - Light variant of the accent color (optional)
 * @param {string} [props.customColor] - Custom hex color (without #) to override accent color
 * @returns {JSX.Element} The rendered card component with styled container
 */
function PromptContentCard({
  children,
  accentColor,
  customColor,
  id,
  isEnclosedMode,
}) {
  return (
    <StyledCard
      id={id}
      accentColor={accentColor}
      customColor={customColor}
      isEnclosedMode={isEnclosedMode}
    >
      {children}
    </StyledCard>
  );
}

PromptContentCard.propTypes = {
  children: PropTypes.node.isRequired,
  accentColor: PropTypes.shape({
    main: PropTypes.string,
    light: PropTypes.string,
  }),
  customColor: PropTypes.string,
  isEnclosedMode: PropTypes.bool,
  id: PropTypes.string,
};

export default memo(PromptContentCard);
